import type { FC } from "react";
import { useTranslation } from "react-i18next";
import SystemDialog from "../../../components/UIToolkit/Popups/Dialog";

/** What would throw the design away: leaving it (another printout or version, closing the popup), or Discard itself. */
export type UnsavedDesignReason = "leave" | "discard";

type Props = {
	open: boolean;
	reason: UnsavedDesignReason;
	/** Annie is still designing (rather than a finished design waiting to be saved). */
	isGenerating: boolean;
	/** A new printout: nothing of it is saved yet, so Discard keeps nothing. */
	isNew: boolean;
	onLeave: () => void;
	onStay: () => void;
};

/** Leaving says the work is lost; Discard also says what leaves the chat, and — on a new printout — that nothing is kept. */
const bodyKeyOf = (reason: UnsavedDesignReason, isGenerating: boolean, isNew: boolean): string => {
	if (reason === "leave") return isGenerating ? "printout.custom.ai.leave.body.generating" : "printout.custom.ai.leave.body";
	if (isGenerating) return isNew ? "printout.custom.ai.discard.body.generating.new" : "printout.custom.ai.discard.body.generating";
	return isNew ? "printout.custom.ai.discard.body.new" : "printout.custom.ai.discard.body";
};

/** Asked before switching printouts, closing the popup or pressing Discard would throw away an unsaved design. */
export const CustomPrintoutLeaveDialog: FC<Props> = ({ open, reason, isGenerating, isNew, onLeave, onStay }) => {
	const { t } = useTranslation();
	const testIdScope = reason === "leave" ? "custom-printout-unsaved" : "custom-printout-discard";
	return (
		<SystemDialog
			open={open}
			onClose={onStay}
			title={t("printout.custom.ai.leave.title")}
			body={t(bodyKeyOf(reason, isGenerating, isNew))}
			confirm={t("printout.custom.ai.leave.confirm")}
			cancel={t("printout.custom.ai.leave.cancel")}
			confirmButtonVariant="delete"
			onConfirm={onLeave}
			initialFocus="cancel"
			dataTestId={`${testIdScope}-dialog`}
			confirmDataTestId={`${testIdScope}-confirm`}
			cancelDataTestId={`${testIdScope}-cancel`}
		/>
	);
};
